import {
  EncumbranceExcelGroup,
  EncumbranceExcelPartnerRef,
  EncumbrancesExcelPreview,
  EncumbrancesExcelRawRow,
  EncumbrancesExcelValidationError,
} from '../types/EncumbrancesExcel';
import {
  buildPartnerByCifMap,
} from './fetchExistingPartnersForEncumbrancesExcel';
import { groupEncumbranceExcelById } from './groupEncumbranceExcelById';
import { mapRawRowsToEncumbranceExcelRows } from './mapRawRowsToEncumbranceExcelRows';
import {
  resolveSharesForEncumbranceGroups,
  getResolvedShareForGroup,
} from './resolveEncumbranceSharesForExcel';

type BuildPreviewParams = {
  rawRows: EncumbrancesExcelRawRow[];
  existingPartners: EncumbranceExcelPartnerRef[];
  societyId: string;
};

const resolvePartnersForGroups = (
  groups: EncumbranceExcelGroup[],
  partnerByCif: Map<string, EncumbranceExcelPartnerRef>
): EncumbranceExcelGroup[] =>
  groups.map((group) => {
    const holderPartner = partnerByCif.get(group.holderCif);
    const errors = [...group.errors];

    if (!holderPartner) {
      errors.push({
        code: 'HOLDER_NOT_FOUND',
        encumbranceId: group.encumbranceId,
        cif: group.holderCif,
      });

      return { ...group, errors };
    }

    group.partyRows.forEach((row) => {
      // External creditors (banks, associations…) are not society partners
      if (row.role === 'CREDITOR') {
        return;
      }

      if (!row.partyCif) {
        return;
      }

      const partyPartner = partnerByCif.get(row.partyCif);
      if (!partyPartner) {
        errors.push({
          code: 'PARTY_NOT_FOUND',
          encumbranceId: group.encumbranceId,
          rowIndex: row.rowIndex,
          cif: row.partyCif,
        });
      }
    });

    return {
      ...group,
      resolvedHolderPartnerId: holderPartner._id,
      errors,
    };
  });

const resolveSharesForGroups = async ({
  groups,
  societyId,
}: {
  groups: EncumbranceExcelGroup[];
  societyId: string;
}): Promise<EncumbranceExcelGroup[]> => {
  const sharesByRangeKey = await resolveSharesForEncumbranceGroups({
    societyId,
    groups,
  });

  return groups.map((group) => {
    if (
      !group.resolvedHolderPartnerId ||
      group.errors.some((error) => error.code === 'HOLDER_NOT_FOUND')
    ) {
      return group;
    }

    const share = getResolvedShareForGroup(
      sharesByRangeKey,
      group.resolvedHolderPartnerId,
      group.shareFrom,
      group.shareTo
    );

    if (!share) {
      return {
        ...group,
        errors: [
          ...group.errors,
          {
            code: 'RANGE_NOT_AVAILABLE',
            encumbranceId: group.encumbranceId,
            cif: group.holderCif,
          },
        ],
      };
    }

    return {
      ...group,
      resolvedShareId: share._id,
    };
  });
};

const finalizeGroups = (
  groups: EncumbranceExcelGroup[]
): EncumbranceExcelGroup[] =>
  groups.map((group) => ({
    ...group,
    isValid: group.errors.length === 0,
  }));

export const buildEncumbrancesExcelPreview = async ({
  rawRows,
  existingPartners,
  societyId,
}: BuildPreviewParams): Promise<EncumbrancesExcelPreview> => {
  const globalErrors: EncumbrancesExcelValidationError[] = [];
  const { rows, errors: rowErrors } = mapRawRowsToEncumbranceExcelRows(rawRows);

  globalErrors.push(...rowErrors);

  let groups = groupEncumbranceExcelById(rows);
  const partnerByCif = buildPartnerByCifMap(existingPartners);

  groups = resolvePartnersForGroups(groups, partnerByCif);
  groups = await resolveSharesForGroups({ groups, societyId });
  groups = finalizeGroups(groups);

  const groupErrors = groups.flatMap((group) => group.errors);
  const allErrors = [...globalErrors, ...groupErrors];

  return {
    groups,
    totalEncumbrances: groups.length,
    totalRows: rows.length,
    totalErrors: allErrors.length,
    validEncumbrances: groups.filter((group) => group.isValid).length,
    errors: allErrors,
  };
};

export const buildEncumbrancesExcelPreviewSync = ({
  rawRows,
  existingPartners,
}: Omit<BuildPreviewParams, 'societyId'>): EncumbrancesExcelPreview => {
  const globalErrors: EncumbrancesExcelValidationError[] = [];
  const { rows, errors: rowErrors } = mapRawRowsToEncumbranceExcelRows(rawRows);

  globalErrors.push(...rowErrors);

  let groups = groupEncumbranceExcelById(rows);
  const partnerByCif = buildPartnerByCifMap(existingPartners);
  groups = resolvePartnersForGroups(groups, partnerByCif);
  groups = finalizeGroups(groups);

  const groupErrors = groups.flatMap((group) => group.errors);
  const allErrors = [...globalErrors, ...groupErrors];

  return {
    groups,
    totalEncumbrances: groups.length,
    totalRows: rows.length,
    totalErrors: allErrors.length,
    validEncumbrances: groups.filter((group) => group.isValid).length,
    errors: allErrors,
  };
};
